import { useContext, useMemo } from 'react';
import { type Theme, StyledEngineProvider, ThemeProvider, createTheme } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import type { AppThemingPalette } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { colors, theme } from './theme';

type Props = {
  children: React.ReactNode;
};

function getLoadingPalette(muiTheme: Theme, wlPalette: AppThemingPalette) {
  const gray = muiTheme.palette.gray;
  return {
    ...muiTheme.palette,
    blue: {
      ...muiTheme.palette.blue,
      ...wlPalette?.blue,
      ...wlPalette?.green,
    },
    primary: {
      ...muiTheme.palette.primary,
      main: gray.main,
      dark: gray.dark1,
      light: gray.light1,
      light1: gray.light1,
      light2: gray.light2,
      light3: gray.light3,
      light4: gray.light4,
      light5: gray.light5,
      light6: gray.light6,
    },
    sentiment: {
      ...muiTheme.palette.sentiment,
      primary: {
        main: gray.main,
        dark: gray.dark1,
        light: gray.light1,
        lighter: gray.light2,
      },
    },
    badge: {
      ...muiTheme.palette.badge,
      citation: {
        bg: gray.light1,
        color: gray.main,
        hover: {
          bg: gray.light2,
          color: gray.main,
        },
      },
    },
    btnPrimary: {
      ...muiTheme.palette.btnPrimary,
      color: gray.main,
      bg: gray.light1,
      bg1: gray.light1,
      border: gray.main,
      active: {
        color: gray.main,
        bg: gray.light2,
        border: gray.main,
      },
    },
    btnSecondary: {
      ...muiTheme.palette.btnSecondary,
      color: colors.gray.main,
      bg: colors.white.main,
      bg1: colors.white.main,
      border: colors.gray.main,
      active: {
        color: colors.gray.main,
        bg: colors.gray.light2,
        border: colors.gray.main,
      },
    },
    loader: {
      main: gray.main,
    },
    skeleton: {
      main: gray.light6,
    },
  };
}

function getWlBadgeAndButtons(muiTheme: Theme, prompt: AppThemingPalette['prompt']) {
  return {
    badge: {
      ...muiTheme.palette.badge,
      citation: {
        bg: prompt?.main,
        color: prompt?.icon,
        hover: {
          bg: prompt?.dark1,
          color: prompt?.icon,
        },
      },
    },
    btnPrimary: {
      ...muiTheme.palette.btnPrimary,
      color: prompt?.icon,
      bg: prompt?.main,
      bg1: prompt?.main,
      border: prompt?.icon,
      active: {
        color: prompt?.icon,
        bg: prompt?.dark1,
        border: prompt?.icon,
      },
    },
    btnSecondary: {
      ...muiTheme.palette.btnSecondary,
      color: colors.gray.main,
      bg: colors.white.main,
      bg1: colors.white.main,
      border: colors.gray.main,
      active: {
        color: colors.gray.main,
        bg: colors.gray.light2,
        border: colors.gray.main,
      },
    },
  };
}

function getWlPrimaryAndSentiment(muiTheme: Theme, primary: AppThemingPalette['primary'], shade: AppThemingPalette['shade']) {
  return {
    primary: {
      ...muiTheme.palette.primary,
      main: primary?.main,
      dark: primary?.dark,
      light: primary?.light,
      light1: shade?.light1,
      light2: shade?.light2,
      light3: shade?.light3,
      light4: shade?.light4,
      light5: shade?.light5,
      light6: shade?.light6,
    },
    sentiment: {
      ...muiTheme.palette.sentiment,
      primary: {
        main: primary?.main,
        dark: primary?.dark,
        light: primary?.light,
        lighter: primary?.lighter,
      },
    },
  };
}

function getWlPalette(muiTheme: Theme, wlPalette: AppThemingPalette) {
  const prompt = wlPalette?.prompt;
  const shade = wlPalette?.shade;
  const primary = wlPalette?.primary;
  const badgeAndButtons = getWlBadgeAndButtons(muiTheme, prompt);
  const primaryAndSentiment = getWlPrimaryAndSentiment(muiTheme, primary, shade);
  return {
    ...muiTheme.palette,
    hyperlink: primary?.main,
    wl: wlPalette,
    blue: {
      ...muiTheme.palette.blue,
      ...wlPalette?.blue,
      ...wlPalette?.green,
    },
    ...primaryAndSentiment,
    ...badgeAndButtons,
    loader: {
      main: primary?.main,
    },
    skeleton: {
      main: prompt?.main,
    },
  };
}

function getWlComponents(muiTheme: Theme, wlPalette: AppThemingPalette) {
  const primary = wlPalette?.primary;
  return {
    ...muiTheme.components,
    MuiTextField: {
      styleOverrides: {
        root: {
          '--TextField-insightsBorderColor': muiTheme.palette.black.light2,
          '--TextField-insightsBorderHoverColor': primary?.light || muiTheme.palette.primary.light1,
          '--TextField-insightsBorderFocusedColor': primary?.main || muiTheme.palette.primary.main,
        },
      },
    },
  };
}

export const MuiThemeProvider = ({ children }: Props) => {

  const muiTheme = createTheme(theme);

  const wl = useContext(AppThemingContext);

  const overwrite = useMemo(() => {
    if (wl.query.isLoading) {
      return {
        ...muiTheme,
        components: {
          ...muiTheme.components,
          MuiTextField: {
            styleOverrides: {
              root: {
                '--TextField-insightsBorderColor': muiTheme.palette.black.light2,
                '--TextField-insightsBorderHoverColor': muiTheme.palette.primary.light1,
                '--TextField-insightsBorderFocusedColor': muiTheme.palette.primary.main,
              },
            },
          },
        },
        palette: getLoadingPalette(muiTheme, wl.palette),
      };
    }

    if (!wl.theme?.enabled) {
      return { ...muiTheme, isTrinityThemed: true };
    }

    return {
      ...muiTheme,
      components: getWlComponents(muiTheme, wl.palette),
      isTrinityThemed: false,
      palette: getWlPalette(muiTheme, wl.palette),
    };
  }, [muiTheme, wl]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={overwrite as Theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          {children}
        </LocalizationProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default MuiThemeProvider;
